// Google Analytics utility functions
declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}

// Push events to GTM dataLayer
const pushToDataLayer = (eventName: string, eventData: Record<string, any> = {}) => {
  if (typeof window === 'undefined') return;
  
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: eventName,
    ...eventData,
    timestamp: new Date().toISOString(),
  });
  
  if (isDebugMode) console.log('GTM: Event pushed to dataLayer', { eventName, eventData });
};

const GA_TRACKING_ID = 'G-KTZD614HK4';

// Check if we're in production environment or Lovable preview
const isProduction = window.location.hostname !== 'localhost' && 
                    !window.location.hostname.includes('127.0.0.1');

// Enable debug mode for development
const isDebugMode = !isProduction;

// Initialize gtag
export const initGA = () => {
  if (typeof window.gtag === 'undefined') {
    if (isDebugMode) console.log('GA: gtag not available');
    return;
  }
  
  window.gtag('config', GA_TRACKING_ID);
  if (isDebugMode) console.log('GA: Initialized with tracking ID:', GA_TRACKING_ID);
};

// Track page views
export const trackPageView = (url: string, title?: string) => {
  if (typeof window.gtag === 'undefined') {
    if (isDebugMode) console.log('GA: trackPageView called but gtag not available', { url, title });
    return;
  }
  
  window.gtag('config', GA_TRACKING_ID, {
    page_location: url,
    page_title: title,
  });
  
  if (isDebugMode) console.log('GA: Page view tracked', { url, title });
};

// Track custom events
export const trackEvent = (action: string, parameters: Record<string, any> = {}) => {
  if (typeof window.gtag === 'undefined') {
    if (isDebugMode) console.log('GA: trackEvent called but gtag not available', { action, parameters });
    return;
  }
  
  // Send to Google Analytics
  window.gtag('event', action, {
    ...parameters,
    custom_parameter: true,
  });
  
  // Also push to GTM dataLayer
  pushToDataLayer(action, parameters);
  
  if (isDebugMode) console.log('GA: Event tracked', { action, parameters });
};

// Predefined event tracking functions
export const analytics = {
  // Lead generation events
  trackQuoteRequest: (serviceType?: string, poolData?: any) => {
    trackEvent('quote_request', {
      event_category: 'lead_generation',
      service_type: serviceType,
      pool_data: poolData ? JSON.stringify(poolData) : undefined,
    });
  },

  trackContactForm: (pageSource: string, campaignSource?: string) => {
    trackEvent('contact_form_submit', {
      event_category: 'lead_generation',
      page_source: pageSource,
      campaign_source: campaignSource,
    });
  },

  trackPhoneCall: () => {
    trackEvent('phone_call', {
      event_category: 'engagement',
    });
  },

  // Service interactions
  trackServiceInterest: (serviceName: string) => {
    trackEvent('service_interest', {
      event_category: 'engagement',
      service_name: serviceName,
    });
  },

  trackPricingView: (planName: string) => {
    trackEvent('pricing_view', {
      event_category: 'engagement',
      plan_name: planName,
    });
  },

  // Navigation events
  trackServiceAreaView: (areaName: string) => {
    trackEvent('service_area_view', {
      event_category: 'navigation',
      area_name: areaName,
    });
  },

  trackResourceView: (resourceName: string) => {
    trackEvent('resource_view', {
      event_category: 'engagement',
      resource_name: resourceName,
    });
  },

  // Calculator usage
  trackCalculatorUse: (calculationType: string, result?: any) => {
    trackEvent('calculator_use', {
      event_category: 'tools',
      calculator_type: calculationType,
      result: result ? JSON.stringify(result) : undefined,
    });
  },

  // QR Code tracking
  trackQRCode: (qrType: string) => {
    trackEvent('qr_code_access', {
      event_category: 'qr_codes',
      qr_type: qrType,
      timestamp: new Date().toISOString(),
    });
  },

  // Work inquiry tracking
  trackWorkInquiry: (inquiryType: string, additionalData?: any) => {
    trackEvent('work_inquiry', {
      event_category: 'lead_generation',
      inquiry_type: inquiryType,
      ...additionalData,
    });
  },

  // Hero form tracking
  trackHeroQuoteRequest: (poolData?: any) => {
    trackEvent('hero_quote_request', {
      event_category: 'lead_generation',
      page_source: 'homepage_hero',
      campaign_source: 'direct',
      pool_data: poolData ? JSON.stringify(poolData) : undefined,
    });
  },
};